/**
 * THE ONE score→position mapping for the organ severity scale.
 *
 * WHY THIS MODULE EXISTS. BandScale positioned three things with three
 * different formulas: segments by inclusive width (`to - from + 1`, summing to
 * 101%), labels by `flex justify-between` (five evenly-spaced items, ignoring
 * the ranges entirely), and the marker by a raw `left: score%`. The bands are
 * NOT equal width -- critical spans 30 points and healthy spans 11 -- so the
 * labels sat under the wrong colours, and the scale disagreed with itself
 * about where a score lived.
 *
 * Everything here derives from SCALE_BANDS, which mirrors the backend's
 * authoritative `config.BANDS`. The frontend never invents a threshold: change
 * the rubric and the segments, the labels and the marker all move together,
 * because they are computed from the same array by the same functions.
 */
import { SCALE_BANDS, type OrganBand } from './organHealth';

/** Lowest and highest score the axis can express. */
export const SCORE_MIN = 0;
export const SCORE_MAX = 100;

export type ScaleSegment = {
  band: OrganBand;
  /** Left edge, as a percentage of the track. */
  startPct: number;
  /** Width, as a percentage of the track. */
  widthPct: number;
  /** Horizontal centre, for the label under this segment. */
  centerPct: number;
  /** Inclusive score range this segment represents. */
  from: number;
  to: number;
};

/**
 * Where a score sits on the track, 0–100, clamped.
 *
 * The axis is linear in the score, so 15 sits at 15% — inside `critical`,
 * which occupies 0–30%. Used by the marker AND by the segment geometry below,
 * so the two can never drift.
 */
export function scoreToPercent(score: number): number {
  if (!Number.isFinite(score)) return 0;
  const clamped = Math.max(SCORE_MIN, Math.min(SCORE_MAX, score));
  return ((clamped - SCORE_MIN) / (SCORE_MAX - SCORE_MIN)) * 100;
}

/**
 * The segments, derived from the band table.
 *
 * A band's inclusive integer range [from, to] occupies the continuous interval
 * [from, nextFrom) — so `critical` (0–29) owns 0–30% and butts exactly against
 * `severe` (30–49) at 30%. The final band runs to SCORE_MAX. Widths therefore
 * sum to exactly 100, not the 101 the old `to - from + 1` produced.
 */
export function scaleSegments(): ScaleSegment[] {
  const ordered = [...SCALE_BANDS].sort((a, b) => a.from - b.from);
  return ordered.map((seg, i) => {
    const next = ordered[i + 1];
    const startScore = seg.from;
    const endScore = next ? next.from : SCORE_MAX;
    const startPct = scoreToPercent(startScore);
    const endPct = scoreToPercent(endScore);
    return {
      band: seg.band,
      from: seg.from,
      to: seg.to,
      startPct,
      widthPct: endPct - startPct,
      centerPct: (startPct + endPct) / 2,
    };
  });
}

/** The segment a score falls in, by the same boundaries the track draws. */
export function segmentForScore(score: number): ScaleSegment | null {
  if (!Number.isFinite(score)) return null;
  const pct = scoreToPercent(score);
  const segs = scaleSegments();
  return segs.find((s) => pct >= s.startPct && pct < s.startPct + s.widthPct) ?? segs[segs.length - 1] ?? null;
}
